
import { Component, Vue } from 'vue-property-decorator'
import { ClueService } from '@/services/index'
import { ElForm } from 'element-ui/types/form'
import { IMG_HOST } from '@/config/env'
import UploadImg from '@/components/upload_img/index.vue'
import { isValidURL } from '@/common/utils'
import { AppModule } from '@/store/modules/app'
import { convertCodeToText, convertTextToCode } from '@/common/area_data'

enum EditType {
    edit = 0,
    create = 1
}

enum TITLE_TYPE {
    edit = '修改渠道列表',
    create = '创建渠道列表'
}

@Component({
    components: {
        UploadImg
    }
})
export default class ChannelList extends Vue {
    private isLoading = true
    private currChannelId = -1
    private sourceInput = ''
    private typeSelect = ''
    private typeInput = ''
    private pageTotal = 0
    private pageIndex = 1
    private pageSize = 10
    private dialogTitle = ''
    private dialogVisible = false
    private editType = EditType.create
    private IMG_HOST = IMG_HOST
    private channel = {
        logo: '',
        title: '',
        official_url: '',
        is_gov: true,
        is_pyppeteer: false,
        province: '',
        city: ''
    }
    private region: any[] = []
    private channelList: any[] = []
    private channelRules = {
        logo: {
            trigger: 'change',
            required: true,
            message: '请上传logo'
        },
        title: { 
            trigger: 'change', 
            validator: this.titleValidator,
            required: true
        },
        official_url: { 
            trigger: 'change', 
            validator: this.urlValidator,
            required: true
        }
    }

    get channelRef() {
        return this.$refs.channelRef as ElForm
    }

    get canDelete() {
        return (channel: any) => {
            return !channel.clue_source_count && !channel.clue_count
        }
    }

    get regionData() {
        return AppModule.provinceAndCity
    }

    private titleValidator(rule: any, value: string, callback: Function) {
        if (!this.dialogVisible) return
        const length = value.length
        let message = ''
        if (!length) {
            message = '请输入渠道名称'
        } else if (length < 2) {
            message = '至少2个字符以上'
        } else if (length > 30) {
            message = '名称在30个字符以内'
        }
        message && callback(new Error(message))
        callback()
    }

    private urlValidator(rule: any, value: string, callback: Function) {
        if (!this.dialogVisible) return
        // 检验网址
        let message = '请输入正确的链接地址'
        if (!value) return callback(message)
        if (!isValidURL(value)) {
            return callback(message)
        }
        callback()
    }

    private async mounted() {
        await this.initData()
        if (!this.regionData.length) {
            AppModule.getRegion()
        }
    }

    private async initData(name: string = '') {
        const params = {
            name: name || undefined,
            page_index: this.pageIndex,
            page_size: this.pageSize
        }
        this.isLoading = true
        const result = await ClueService.getChannelList(params)
        this.isLoading = false
        if (result.status === 200) {
            this.channelList = result.data.list
            this.pageTotal = result.data.page_total
        }
    }

    private uploadCallback(url: string) {
        const $channelRef = this.$refs.channelRef as ElForm
        this.channel.logo = url
        $channelRef.validateField('logo')
    }

    private async updateChannel() {
        this.isLoading = true
        const params = {
            id: this.currChannelId,
            logo: this.channel.logo,
            name: this.channel.title,
            official_url: this.channel.official_url,
            is_gov: this.channel.is_gov,
            is_pyppeteer: this.channel.is_pyppeteer,
            province: this.channel.province,
            city: this.channel.city
        }
        const result = await ClueService.updateChannel(params)
        this.isLoading = false
        if (result.status === 200) {
            this.$message({
                type: 'success',
                message: '修改成功'
            })
            return true
        }
        return false
    }

    private async createChannel() {
        const params = { 
            logo: this.channel.logo,
            name: this.channel.title,
            official_url: this.channel.official_url,
            is_gov: this.channel.is_gov,
            is_pyppeteer: this.channel.is_pyppeteer,
            province: this.channel.province,
            city: this.channel.city
        }
        this.isLoading = true
        const result = await ClueService.createChannel(params)
        this.isLoading = false
        if (result.status === 200) {
            this.$message({
                type: 'success',
                message: '创建数据源分类成功'
            })
            return true
        }
        return false
    }

    private async delete(id: number) {
        const params = { id }
        this.isLoading = true
        const result = await ClueService.deleteChannel(params)
        this.isLoading = false
        if (result.status === 200) {
            this.$message({
                type: 'success',
                message: '删除成功',
                showClose: true
            })
            this.initData()
        }
    }

    private onDialogOpen() {
        this.dialogTitle = this.editType === EditType.create ? 
            TITLE_TYPE.create : TITLE_TYPE.edit
    }

    private onDialogClosed() {
        this.channel.title = ''
        this.channel.official_url = ''
        this.channelRef.clearValidate()
    }

    private onCreateChannel() {
        this.region = []
        this.editType = EditType.create
        this.dialogVisible = true
    }

    private onSetDataSource(channel: any) {
        this.$router.push({ 
            name: 'SourceList',
            params: {
                id: channel.id
            },
            query: {
                name: encodeURIComponent(channel.name)
            }
        })
    }

    private onPageChange(curPage: number) {
        this.pageIndex = curPage
        this.initData()
    }

    private onUpdate(channel: any) {
        this.editType = EditType.edit
        this.currChannelId = channel.id
        this.channel.logo = channel.logo.replace(this.IMG_HOST, '')
        this.channel.title = channel.name
        this.channel.official_url = channel.official_url
        this.channel.is_gov = channel.is_gov
        this.channel.is_pyppeteer = channel.is_pyppeteer
        this.channel.province = channel.province
        this.channel.city = channel.city
        this.region = convertTextToCode([channel.province, channel.city])
        this.dialogVisible = true
    }

    private async onDelete(id: number) {
        this.$confirm('是否确定删除?', '提示', {
            confirmButtonText: '确定',
            cancelButtonText: '取消',
            type: 'warning'
        }).then(() => {
            this.delete(id)
        })
    }

    private onConfirm() {
        this.channelRef.validate(async (isValid: boolean) => {
            if (isValid) {
                let result = false
                switch (this.editType) {
                    case EditType.edit:
                        result = await this.updateChannel()
                        break 
                    case EditType.create:
                        result = await this.createChannel()
                        break
                    default:
                        break
                }
                result && this.initData()
                this.dialogVisible = false
            }
        })
    }

    private onSearchChannel() {
        this.pageIndex = 1;
        this.initData(this.typeInput)
    }

    private onSelectRegion(options: any) {
        this.region = options
        if (!options || !options.length) {
            this.channel.province = ''
            this.channel.city = ''
        }
        const _region = convertCodeToText(options)
        if (_region) {
            const areaData  = _region.split(',')
            if (areaData.length === 2) {
                this.channel.province = areaData[0]
                this.channel.city = areaData[1]
            } else {
                this.channel.province = _region
                this.channel.city = ''
            }
        }
    }
}
